import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Select, MenuItem, FormControl } from '@mui/material';
import styles from "./css/CityViewPage.module.css";
import { DashboardCard } from "@/components/cards/DashboardCard";
import GeneralTab from "@/components/tabs/GeneralTabs";
import { CityAttributes, Favorites, Neighborhood, Tab, UpdateCityFavoritesData } from "@/types";
import { CityInfoTab } from "@/components/pageParts/cityView/CityInfoTab";
import { GeneralEnergyTab } from "@/components/pageParts/cityView/GeneralEnergyTab";
import { ElectricityTab } from "@/components/pageParts/cityView/ElectricityTab";
import { TransportationTab } from "@/components/pageParts/cityView/TransportationTab";

import { RenewalEnergeyTab } from "@/components/pageParts/cityView/RenewalEnergeyTab";
import { RootState } from "@/store/store";
import { useSelector, useDispatch } from "react-redux";
import { SearchInput } from "@/components/inputFields/SearchInput";
import { useGetCityAttributes } from "@/hooks/cityAttribtes/useGetCityAttribute";
import { getAllSlots, getSlot } from "@/api/cityAttributes";
import { FavoriteTab } from "@/components/pageParts/cityView/FavoriteTab";
import { LoadingPageAnimation } from "@/components/addons/LoadingPageAnimation";
import { useUpdateCityFavorites } from "@/hooks/city/useUpdateCityFavorites";
import { setUserEntity } from "@/store/entitySlice";
import { EntityInfoSection } from "@/components/pageParts/cityView/EntityInfoSection";
import { ConsumptionData } from "@/types";
import { consumptionData as cd } from "@/mocks/consumption";

import { GIS } from "@/GIS/GIS";
import { verify } from "crypto";
export const CityViewPage: React.FC = () => {
  const { t } = useTranslation('CityViewPage');
  const [selectedTab, setSelectedTab] = useState<string>("info");
  const city = useSelector((state: RootState) => state.entity.userCity);
  const [cityAttributes, setCityAttributes] = useState<CityAttributes | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<number>(1); // Default to slot 1
  const [availableSlots, setAvailableSlots] = useState<{slot: number, name: string, isEmpty: boolean, isValidated: boolean}[]>([]);
  const [consumptionData, setConsumptionData] = useState<ConsumptionData | null>(cd);
  const dispatch = useDispatch();
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<Neighborhood | null>(null);
  const [favorites, setFavorites] = useState<Favorites>(city?.favorites || {
    residents: false,
    vehicles: false,
    apartments: false,
    usage: false,
    energy: false,
    energyCreation: false,
    energyConsumption: false,
    energyConsumption_future: false,
    electricalInfrastructure: false,
    greenhouseEmissions: false,
    renewableEnergyTarget: false,
    renewableEnergyProduction: false,
    energyFromRenewableSources: false,
    storageFacilities: false,
    energyProductionPotential: false,
    numberOfVehicles: false,
    numberOfElectricVehicles: false,
    chargingStations: false,
    existingCapacity: false
  });

  const updateCityFavorites = useUpdateCityFavorites({
    onSuccess: () => {
      const cityData = {
        ...city,
        favorites: favorites
      }
      dispatch(setUserEntity({
        entityType: 'city',
        userCity: cityData
      }));
    }
  });
  const getCityAttributes = useGetCityAttributes({
    onSuccess: (data) => {
      setCityAttributes(data);
    },
    onError: (error) => {
      console.error("Error in get City Attributes");
    }
  });

  useEffect(() => {
    if (city) {
      setFavorites(city.favorites);
      fetchAllSlots();
    }
  }, [city]);

  // Fetch all slots to check which ones have data
  const fetchAllSlots = async () => {
    if (!city?.id) return;
    
    try {
      const slots = await getAllSlots({ city_id: city.id });
      const SLOT_NAMES: Record<number, string> = {
        1: t('slotName1'),
        2: t('slotName2'),
        3: t('slotName3')
      };
      
      const slotsInfo = slots.map((slot: any) => ({
        slot: slot.slot_number,
        name: SLOT_NAMES[slot.slot_number as 1 | 2 | 3] || t('slotFallback', { number: slot.slot_number }),
        isEmpty: slot.is_empty || false,
        isValidated: slot.is_validated || false
      }));
      
      setAvailableSlots(slotsInfo);
      
      // Fetch data for default slot (slot 1)
      fetchSlotData(1);
    } catch (error) {
      console.error("Error fetching slots:", error);
      // Fallback to fetching slot 1 only
      fetchSlotData(1);
    }
  };

  // Fetch data for specific slot
  const fetchSlotData = async (slotNumber: number) => {
    if (!city?.id) return;
    
    try {
      const data = await getSlot({ city_id: city.id, slot_number: slotNumber });
      setCityAttributes(data);
    } catch (error) {
      console.error(`Error fetching slot ${slotNumber}:`, error);
    }
  };

  // Handle slot change
  const handleSlotChange = (slotNumber: number) => {
    setSelectedSlot(slotNumber);
    fetchSlotData(slotNumber);
  };

  const tabs: Tab[] = [
    {
        name: t("favorites"),
        key: "favorites"
    },
    {
        name: t("info"),
        key: "info"
    },
    {
        name: t("generalEnergy"),
        key: "generalEnergy"
    },
    {
        name: t("transportation"),
        key: "transportation"
    },
    {
        name: t("electricity"),
        key: "electricity"
    }
  ]

  useEffect(() => {
    if (city  && city.favorites !== favorites) {
      const body: UpdateCityFavoritesData = {
        city_id: city.id,
        favorites: favorites
      }
      updateCityFavorites.mutate(body);
    }
  }, [favorites]);



  const cityAttributesColors = {
    vehicles: {
      'כמות רכבים': '#AF52DE',
    },
    residents: {
      'כמות תושבים': '#007AFF',
    },
    apartments: {
      'כמות יח״ד': '#00C7BE',
    },
    usage: {
      'שימושים אחרים': '#34C759',  // Green
      'ציבורי': '#007AFF',          // Blue
      'מסחר': '#32ADE6',          // Light Blue
      'תעשייה': '#FFCC00',         // Yellow
      'תעסוקה': '#FF2D55',           // Red
      'מגורים': '#AF52DE',         // Purple
    },
    gas_emissions: {
      'פליטות גז״ח': '#007AFF'
    },
    electric_vehicles: {
      'רכבים חשמליים': '#007AFF'
    },
    ev_charging_stations: {
      'עמדות טעינה': '#32ADE6',
      'כמות עמדות AC ציבורי נדרשת כיום ': '#AF52DE',
    },
    charging_providers: {
      "הספקים": "#34c759"
    },
    energy_consumption: {
      "מגורים": "#007AFF",
      "מסחר": "#32ADE6",
      "תעשייה": "#FFCC00"
    },
    electricity: {
    "הספק חשמלי במרחב מגורים": "#4A90E2",
    "הספק חשמלי שימושים אחרים": "#50C878",
    "הספק חשמלי טעינת רכבים": "#F5A623",
    "הספק חשמלי לפי חברת חשמל": "#9013FE",
    "מספר תחמשים": "#B8E986"
  }
  }

  const filterNeighborhoods = (searchText: string, neighborhoods: Neighborhood[]) => {
    return neighborhoods.filter(neighborhood => neighborhood.name.toLowerCase().includes(searchText.toLowerCase()));
  }

  const handleSelectNeighborhood = (neighborhood: Neighborhood | null) => {
    setSelectedNeighborhood(neighborhood);
  }

  useEffect(() => {
    if(selectedNeighborhood) {
       getCityAttributes.mutate({city_id: city?.id as string, neighborhood_id: selectedNeighborhood.id});
    }
    else {
      getCityAttributes.mutate({city_id: city?.id as string});
    }
  }, [selectedNeighborhood]);

  const fetchNeighborhoodDetails = (id: string | number) => {
    console.log(`Fetching details for neighborhood ${id}`);
  };
  
  const showBuildingDetails = (id: string | number, properties: any) => {
    console.log(`Showing details for building ${id}`, properties);
  };

  const handleFeatureClick = (data: { type: string; id: string | number; properties: any }) => {
    if (data.type === 'neighborhood') {
      fetchNeighborhoodDetails(data.id);
    } else if (data.type === 'building') {
      showBuildingDetails(data.id, data.properties);
    }
  };

  return (
    <>
      {city ? (
        <div className={styles.container}>
          <div className={styles.headerSection}>
            <EntityInfoSection entity={city}/>
            {/* Slot Selector Dropdown */}
            <div className={styles.slotSelector}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={selectedSlot}
                  onChange={(e) => handleSlotChange(e.target.value as number)}
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#C3CFE7',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#007AFF',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#007AFF',
                    },
                  }}
                >
                  {availableSlots.map((slot) => (
                    <MenuItem 
                      key={slot.slot} 
                      value={slot.slot}
                      disabled={!slot.isValidated}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      {slot.name}
                      {slot.slot === 1 && (
                        <span style={{ 
                          fontSize: '11px', 
                          color: '#007AFF',
                          marginRight: '8px',
                          fontWeight: 500
                        }}>
                          ({t('activeData')})
                        </span>
                      )}
                      {!slot.isValidated && (
                        <span style={{ 
                          fontSize: '11px', 
                          color: '#999',
                          marginRight: '8px'
                        }}>
                          ({t('invalidSlot')})
                        </span>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
           {/* TODO: when the search needed - remove style & remove the comment in line 190 */}
            <div className={styles.dashboard} style={{marginTop: "-5px"}}>
            {/* <DashboardCard middleTab={true} tabContent={<SearchInput id="search" options={[]} onSelect={handleSelectNeighborhood} placeholder="חפש שכונה" filterFunction={filterNeighborhoods}/>} */}
                <DashboardCard children={
                    <div className={styles.dashboardContent}>
                         <div className={styles.tabsContent}>
                            <div className={styles.tabs}>
                                <GeneralTab tabs={tabs} activeTab={selectedTab} onTabChange={setSelectedTab}/>
                            </div>
                            <div className={styles.tabContentContainer}>
                                {selectedTab === "favorites" && <FavoriteTab consumptionData={consumptionData} cityAttributesColors={cityAttributesColors} cityAttributes={cityAttributes} favorites={favorites} setFavorites={setFavorites}/>}
                                {selectedTab === "info" && <CityInfoTab colors={cityAttributesColors} cityAttributes={cityAttributes} favorites={favorites} setFavorites={setFavorites}/>}
                                {selectedTab === "generalEnergy" && <GeneralEnergyTab colors={cityAttributesColors} cityAttributes={cityAttributes} favorites={favorites} setFavorites={setFavorites}/>}
                                {/* {selectedTab === "renualEneregy" && <RenewalEnergeyTab favorites={favorites} setFavorites={setFavorites}/>} */}
                                {selectedTab === "transportation" && <TransportationTab favorites={favorites} setFavorites={setFavorites} cityAttributes={cityAttributes} colors={cityAttributesColors}/>}
                                {selectedTab === "electricity" && <ElectricityTab favorites={favorites} setFavorites={setFavorites} cityAttributes={cityAttributes} colors={cityAttributesColors}/>}

                            </div>
                        </div>
                        <div className={styles.mapContent}>
                          <GIS city={city} onFeatureClick={handleFeatureClick}></GIS>
                        </div>
                    </div>
                }/>
            </div>
          </div>
      ) : (
        <LoadingPageAnimation/>
      )}
    </>
  );
};
